import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  Observable,
  split,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ENV } from "../utils/configs";
import { cache } from "../utils/cache";
import axios from "axios";
import { from, throwError } from "rxjs";
import { switchMap, catchError } from "rxjs/operators";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { StorageHelper } from "../utils/storageHelper";

let isRefreshing = false;
let tokenSubscribers = new Map();
let failedOperations = new Set();
let currentToken = StorageHelper.get("token");
let pendingOperations = new Map();
let apolloClient = null;
let refreshPromise = null;
let retriedOperations = new Set(); // Track operations that have been retried to prevent duplicates

const getOperationKey = (operation) => {
  return `${operation.operationName}:${JSON.stringify(operation.variables)}`;
};

// Helper function to retry a single operation after token refresh
const retryOperation = (key, operation, forward, observer, token, onComplete) => {
  // Prevent duplicate retries
  if (retriedOperations.has(key)) {
    // console.log(`⚠️ [SKIP] Operation already retried: ${key}`);
    if (onComplete) {
      onComplete();
    }
    return;
  }

  retriedOperations.add(key);
  // console.log(`🔄 [RETRY] Retrying operation: ${key}`);

  // Update operation context with new token
  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      authorization: `Bearer ${token}`,
    },
  });

  // Retry the operation
  forward(operation).subscribe({
    next: (result) => {
      failedOperations.delete(key);
      retriedOperations.delete(key); // Remove from retried set after success
      // console.log(`✅ [SUCCESS] Operation completed: ${key}`);

      // Update cache with the new data
      if (result.data && apolloClient) {
        try {
          // Write the result to cache using writeQuery
          // This ensures Apollo Client properly updates the cache and triggers re-renders
          const query = operation.query;
          const variables = operation.variables || {};

          // Write query result to cache
          apolloClient.cache.writeQuery({
            query,
            variables,
            data: result.data,
          });

          // console.log(`💾 [CACHE] Cache updated for operation: ${key}`);
        } catch (error) {
          console.error("❌ [CACHE] Error updating cache:", error);
          // Even if cache update fails, we should still emit the result
        }
      }

      // Emit result immediately to the observer for this specific operation
      // This triggers the component to re-render with new data
      if (observer) {
        if (typeof observer.next === 'function') {
          observer.next(result);
        }
        // Only call complete if it exists and is a function
        if (typeof observer.complete === 'function') {
          observer.complete();
        }
      }

      // Call completion callback if provided
      if (onComplete) {
        onComplete();
      }
    },
    error: (error) => {
      retriedOperations.delete(key); // Remove from retried set on error
      // console.log(`❌ [ERROR] Operation failed: ${key}`, error);
      
      // Emit error to the observer for this specific operation
      if (observer && typeof observer.error === 'function') {
        observer.error(error);
      }

      // Call completion callback even on error
      if (onComplete) {
        onComplete();
      }
    },
  });
};

let sharedRefreshPromise = null;

const performRefreshToken = async () => {
  try {
    const token = StorageHelper.get("token");
    if (!token) {
      // console.log("❌ [REFRESH] No token found, redirecting to login");
      window.open("/login", "_self");
      throw new Error("No token");
    }

    // console.log("🔄 [REFRESH] Requesting new token");
    const response = await axios.post(
      ENV.api_url,
      {
        query: `
          mutation RefreshToken {
            RefreshToken {
              access_token
            }
          }
        `,
        operationName: "RefreshToken",
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          hostname: window.location.hostname,
        },
      }
    );

    const error = response.data?.errors;
    if (error) {
      console.log("❌ [REFRESH] Server returned error", error);
      window.open("/login", "_self");
      throw new Error(error);
    }

    const new_token = response.data?.data?.RefreshToken?.access_token;
    if (new_token) {
      StorageHelper.set("token", new_token);
      currentToken = new_token;
    }

    return new_token;
  } catch (error) {
    if (error?.response?.status === 401) {
      window.open("/login", "_self");
    }
    console.error("❌ [REFRESH] Token refresh failed:", error);
    throw error;
  }
};

// Coalesce concurrent refreshes (Apollo errorLink + chat stream fetch) into a
// single network call. Keycloak rotates refresh tokens, so parallel refreshes
// would invalidate each other and force a spurious logout to /login.
export const refreshToken = () => {
  if (!sharedRefreshPromise) {
    sharedRefreshPromise = performRefreshToken().finally(() => {
      sharedRefreshPromise = null;
    });
  }
  return sharedRefreshPromise;
};

class ApolloService {
  constructor() {
    if (!ApolloService.instance) {
      this.initializeClient();
      ApolloService.instance = this;
    }
    return ApolloService.instance;
  }

  initializeClient() {
    // Create HTTP link
    const httpLink = createHttpLink({
      uri: ENV.api_url,
    });

    // Create WebSocket link
    const wsLink = new GraphQLWsLink(
      createClient({
        url: ENV.socket_url,
        connectionParams: () => ({
          authorization: `Bearer ${currentToken || StorageHelper.get("token") || ""}`,
          hostname: window.location.hostname,
        }),
      })
    );

    // Split links for subscriptions
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      httpLink
    );

    const authLink = setContext((_, { headers }) => {
      const token = StorageHelper.get("token");
      currentToken = token;

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
          hostname: window.location.hostname,
        },
      };
    });

    // Error link for handling auth errors
    const errorLink = onError(
      ({ graphQLErrors, networkError, operation, forward }) => {
        const isUnauthorized =
          (graphQLErrors && graphQLErrors.some((e) => e?.extensions?.code === "UNAUTHENTICATED")) ||
          (networkError && (networkError.statusCode === 401 || networkError.response?.status === 401));

        if (isUnauthorized) {
          const operationKey = getOperationKey(operation);
          failedOperations.add(operationKey);

          return new Observable((observer) => {
                // Prevent duplicate operations - if already in pending, just add observer
                if (pendingOperations.has(operationKey)) {
                  // console.log(
                  //   `⚠️ [DEDUP] Operation already queued, reusing: ${operationKey}`
                  // );
                  const existing = pendingOperations.get(operationKey);
                  // Add observer to existing operation's observer list
                  if (!existing.observers) {
                    existing.observers = existing.observer ? [existing.observer] : [];
                    delete existing.observer; // Remove single observer property
                  }
                  existing.observers.push(observer);
                  
                  // Wait for refresh to complete - operation will be retried by main handler
                  if (refreshPromise) {
                    refreshPromise
                      .then(() => {
                        // Operation will be handled by the main refresh handler
                      })
                      .catch((error) => {
                        observer.error(error);
                      });
                  }
                  return;
                }

                // If refresh is already in progress, wait for it and queue this operation
                if (isRefreshing && refreshPromise) {
                  // console.log(
                  //   `⏳ [QUEUE] Adding operation to queue: ${operationKey}`
                  // );
                  pendingOperations.set(operationKey, { 
                    operation, 
                    forward, 
                    observers: [observer] 
                  });
                  
                  // Wait for the refresh to complete - operation will be retried in main handler
                  refreshPromise
                    .then(() => {
                      // Operation will be handled by the main refresh handler
                    })
                    .catch((error) => {
                      observer.error(error);
                    });
                  return;
                }

                // Start a new refresh process
                if (!isRefreshing) {
                  isRefreshing = true;
                  retriedOperations.clear(); // Clear retried operations set for new refresh cycle
                  // console.log("🔄 [REFRESH] Starting token refresh");

                  // Store this operation
                  pendingOperations.set(operationKey, { 
                    operation, 
                    forward, 
                    observers: [observer] 
                  });

                  // Create refresh promise
                  refreshPromise = refreshToken()
                    .then((token) => {
                      // console.log("✅ [REFRESH] Token refresh successful");
                      currentToken = token;

                      // Get snapshot of operations to retry (to avoid issues with concurrent additions)
                      const operations = Array.from(pendingOperations.entries());
                      let completed = 0;
                      const totalOperations = operations.length;

                      if (totalOperations === 0) {
                        isRefreshing = false;
                        refreshPromise = null;
                        retriedOperations.clear();
                        return token;
                      }

                      // Function to check if all operations are complete
                      const checkCompletion = () => {
                        completed++;
                        if (completed >= totalOperations) {
                          // console.log(
                          //   "🔄 [REFRESH] All operations completed"
                          // );
                          isRefreshing = false;
                          refreshPromise = null;
                          retriedOperations.clear();
                          pendingOperations.clear();
                          
                          // Don't call reFetchObservableQueries() as it causes duplicate calls
                          // Cache updates and observer emissions already trigger UI updates
                          
                          // console.log(
                          //   "❌ [FAILED] Remaining failed operations:",
                          //   Array.from(failedOperations)
                          // );
                        }
                      };

                      // Retry each operation only once
                      operations.forEach(([key, { operation, forward, observers }]) => {
                        // Create a multi-observer wrapper to emit to all observers
                        const multiObserver = {
                          next: (result) => {
                            // Emit to all observers for this operation
                            if (observers && Array.isArray(observers)) {
                              observers.forEach(obs => {
                                try {
                                  if (obs && typeof obs.next === 'function') {
                                    obs.next(result);
                                  }
                                } catch (err) {
                                  console.error('Error calling observer.next:', err);
                                }
                              });
                            }
                          },
                          error: (error) => {
                            // Emit error to all observers
                            if (observers && Array.isArray(observers)) {
                              observers.forEach(obs => {
                                try {
                                  if (obs && typeof obs.error === 'function') {
                                    obs.error(error);
                                  }
                                } catch (err) {
                                  console.error('Error calling observer.error:', err);
                                }
                              });
                            }
                          },
                          complete: () => {
                            // Call complete on all observers that support it
                            if (observers && Array.isArray(observers)) {
                              observers.forEach(obs => {
                                try {
                                  if (obs && typeof obs.complete === 'function') {
                                    obs.complete();
                                  }
                                } catch (err) {
                                  console.error('Error calling observer.complete:', err);
                                }
                              });
                            }
                          }
                        };

                        retryOperation(key, operation, forward, multiObserver, token, checkCompletion);
                      });

                      return token;
                    })
                    .catch((error) => {
                      console.log("❌ [REFRESH] Token refresh failed", error);
                      
                      // Emit error to all pending observers
                      pendingOperations.forEach(({ observers }) => {
                        if (observers && Array.isArray(observers)) {
                          observers.forEach(obs => {
                            if (obs) {
                              obs.error(error);
                            }
                          });
                        }
                      });
                      
                      isRefreshing = false;
                      refreshPromise = null;
                      retriedOperations.clear();
                      pendingOperations.clear();
                      throw error;
                    });
                }
              });
        }

        if (networkError && !isUnauthorized) {
          const operationKey = getOperationKey(operation);
          failedOperations.add(operationKey);
          console.error(`[Network error]:`, networkError);
          console.log(
            "❌ [FAILED] Current failed operations:",
            Array.from(failedOperations)
          );
        }

        return forward(operation);
      }
    );

    // Create the link chain
    const link = ApolloLink.from([errorLink, authLink, splitLink]);

    // Create Apollo Client instance
    apolloClient = new ApolloClient({
      link,
      cache,
    });
  }

  getClient() {
    return apolloClient;
  }
}

// Create and export a single instance
const apolloService = new ApolloService();
export const client = apolloService.getClient();
